/// <reference types="vite/client" />
/**
 * <LiveIndicator /> — Phase 13 U3 #295 : driven by the WS connection
 * state from useAgentSocketContext rather than the polling lastUpdatedAt.
 *
 * Four states surfaced :
 *   - connecting    (yellow, "Connecting…")
 *   - connected     (green, "Live · Xs")
 *   - reconnecting  (amber, "Reconnecting…")
 *   - disconnected  (red, "Offline")
 *
 * The "Xs" suffix reads lastEventAt from the hook, so the operator
 * sees how recently the last realtime event landed.
 */

import { useEffect, useState } from "react";
import { useAgentSocketContext } from "../hooks/useAgentSocketContext";

function formatAge(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
  return `${Math.floor(seconds / 3600)}h`;
}

const COPY = {
  connecting:    { label: "Connecting…",   color: "var(--color-status-running)", glow: false },
  connected:     { label: "Live",          color: "var(--color-status-active)",  glow: true  },
  reconnecting:  { label: "Reconnecting…", color: "var(--color-status-paused)",  glow: false },
  disconnected: { label: "Offline",       color: "var(--color-status-error)",   glow: false },
} as const;

export function LiveIndicator() {
  const { state, lastEventAt } = useAgentSocketContext();
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const copy = COPY[state];
  const ageSec = lastEventAt ? Math.max(0, Math.floor((now - lastEventAt) / 1000)) : null;

  return (
    <div className="flex items-center gap-2" style={{ color: copy.color }}>
      <span
        className="inline-block w-1.5 h-1.5 rounded-full"
        style={{
          background: copy.color,
          boxShadow: copy.glow ? `0 0 6px ${copy.color}` : "none",
        }}
      />
      <span className="font-mono text-[10px] tracking-[0.18em] uppercase font-medium">
        {copy.label}
        {ageSec !== null && state === "connected" && (
          <span className="text-[var(--color-text-dim)] ml-1">· {formatAge(ageSec)}</span>
        )}
      </span>
    </div>
  );
}
